import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronRight, Send, Sparkles, Bot,
  Download, FileText, CheckCircle, AlertCircle,
  Info, Settings, Database
} from 'lucide-react';
import { colors, agentColors } from '../../constants/theme';
import { AgentId } from '../../types/agent.types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

interface AIAssistantPanelProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  activeAgent: AgentId;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'ai' | 'system' | 'action';
  message: string;
  timestamp: Date;
  actionType?: string;
  actionResult?: any;
}

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  isOpen,
  setIsOpen,
  activeAgent
}) => {
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  useEffect(() => {
    // エージェントが変わったときにウェルカムメッセージを表示
    setChatHistory([{
      id: 'welcome',
      type: 'ai',
      message: `${getAgentName(activeAgent)}エージェントのAIアシスタントです。何かお手伝いできることはありますか？\n\n💡 使えるコマンド例:\n${getCommandExamples().join('\n')}`,
      timestamp: new Date()
    }]);
  }, [activeAgent]);

  const getAgentName = (agent: AgentId) => {
    const names: Record<AgentId, string> = {
      'demand': '需要予測',
      'md': '52週MD',
      'inventory': '在庫最適化',
      'planogram': '棚割り改善',
      'creative': 'クリエイティブ',
      'marketing': 'マーケティング',
      'alerts': 'アラート'
    };
    return names[agent] || 'AI';
  };

  const getCommandExamples = () => {
    const examples: Record<AgentId, string[]> = {
      'demand': [
        '• 「予測を実行」 - 需要予測を開始',
        '• 「CSVでエクスポート」 - データをCSV形式で出力',
        '• 「天気の影響を分析」 - 天気補正を適用'
      ],
      'md': [
        '• 「計画を生成」 - 52週計画を作成',
        '• 「価格を最適化」 - 価格戦略を最適化',
        '• 「変更を適用」 - 計画を確定'
      ],
      'inventory': [
        '• 「在庫を最適化」 - 在庫レベルを最適化',
        '• 「発注を提案」 - 発注推奨リストを作成',
        '• 「ABC分析を実行」 - 商品をABC分類'
      ],
      'planogram': [
        '• 「棚割りを最適化」 - 最適な商品配置を計算',
        '• 「ビジュアルを表示」 - 3D棚割りを確認',
        '• 「PGファイルを出力」 - 棚割りデータを出力'
      ],
      'creative': [
        '• 「デザインを生成」 - クリエイティブを作成',
        '• 「バッチ処理を開始」 - 複数デザインを一括生成',
        '• 「印刷用に出力」 - 印刷用データを生成'
      ],
      'marketing': [
        '• 「キャンペーンを作成」 - 新規キャンペーンを企画',
        '• 「ROIを分析」 - 投資対効果を分析',
        '• 「顧客をセグメント」 - 顧客層を分析'
      ],
      'alerts': [
        '• 「アラートを設定」 - 通知ルールを設定',
        '• 「テスト通知」 - 通知機能をテスト',
        '• 「履歴を確認」 - 過去のアラートを表示'
      ]
    };
    return examples[activeAgent] || ['• コマンドを入力してください'];
  };

  const executeCommand = (command: string) => {
    const lowerCommand = command.toLowerCase();
    let actionResult: any = null;
    let actionType = '';

    // コマンドを解析して実行
    if (lowerCommand.includes('予測') && (lowerCommand.includes('実行') || lowerCommand.includes('開始'))) {
      actionType = '需要予測実行';
      actionResult = {
        summary: '需要予測を実行しました',
        details: {
          期間: '2025年1月22日〜2月22日',
          対象SKU数: 2345,
          予測精度: '92.3%',
          上昇トレンドSKU: 234,
          下降トレンドSKU: 89,
          推奨アクション: '飲料カテゴリの発注量を20%増加'
        }
      };
    } else if (lowerCommand.includes('csv') || lowerCommand.includes('エクスポート')) {
      actionType = 'データエクスポート';
      actionResult = {
        summary: 'データをエクスポートしました',
        details: {
          ファイル名: `${activeAgent}_data_${new Date().toISOString().split('T')[0]}.csv`,
          レコード数: 2345,
          ファイルサイズ: '1.2MB'
        }
      };
    } else if (lowerCommand.includes('計画') && lowerCommand.includes('生成')) {
      actionType = '計画生成';
      actionResult = {
        summary: '52週MD計画を生成しました',
        details: {
          計画期間: '2025年度',
          カテゴリ数: 18,
          粗利率改善: '+2.3%',
          在庫回転率: '12.5回/年'
        }
      };
    } else if (lowerCommand.includes('価格') && lowerCommand.includes('最適化')) {
      actionType = '価格最適化';
      actionResult = {
        summary: '価格最適化を実行しました',
        details: {
          対象商品数: 456,
          平均値下げ率: '8.5%',
          予測粗利改善: '+¥2.3M'
        }
      };
    } else if (lowerCommand.includes('在庫') && lowerCommand.includes('最適化')) {
      actionType = '在庫最適化';
      actionResult = {
        summary: '在庫最適化を完了しました',
        details: {
          最適化SKU数: 1234,
          在庫削減額: '¥15.2M',
          欠品リスク削減: '82%'
        }
      };
    } else if (lowerCommand.includes('発注') && lowerCommand.includes('提案')) {
      actionType = '発注提案';
      actionResult = {
        summary: '発注提案を作成しました',
        details: {
          発注対象: '234品',
          発注金額: '¥8.5M',
          緊急発注: '12品'
        }
      };
    } else if (lowerCommand.includes('abc') || lowerCommand.includes('分析')) {
      actionType = 'ABC分析';
      actionResult = {
        summary: 'ABC分析を実行しました',
        details: {
          A群: '234品(売上70%)',
          B群: '456品(売上20%)',
          C群: '544品(売上10%)'
        }
      };
    } else if (lowerCommand.includes('棚割り') && lowerCommand.includes('最適化')) {
      actionType = '棚割り最適化';
      actionResult = {
        summary: '棚割り最適化を完了しました',
        details: {
          対象什器数: 24,
          商品移動数: 156,
          売上向上予測: '+8.5%'
        }
      };
    } else if (lowerCommand.includes('キャンペーン') && lowerCommand.includes('作成')) {
      actionType = 'キャンペーン作成';
      actionResult = {
        summary: 'キャンペーンを作成しました',
        details: {
          期間: '3月1日〜3月31日',
          予算: '¥5.0M',
          ROI予測: '500%'
        }
      };
    }

    return { actionType, actionResult };
  };

  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chatInput.trim() && !isProcessing) {
      const userMessage = chatInput.trim();
      const newUserMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        message: userMessage,
        timestamp: new Date()
      };

      setChatHistory(prev => [...prev, newUserMessage]);
      setChatInput('');
      setIsProcessing(true);
      setShowSuggestions(false);

      // コマンド実行
      setTimeout(() => {
        const { actionType, actionResult } = executeCommand(userMessage);
        
        if (actionResult) {
          // アクション実行メッセージ
          const actionMessage: ChatMessage = {
            id: Date.now().toString() + '_action',
            type: 'action',
            message: actionResult.summary,
            timestamp: new Date(),
            actionType,
            actionResult
          };
          setChatHistory(prev => [...prev, actionMessage]);
        } else {
          // 通常のAI応答
          const aiMessage: ChatMessage = {
            id: Date.now().toString() + '_ai',
            type: 'ai',
            message: `「${userMessage}」について処理しています。どのような操作をご希望ですか？\n\n使用可能なコマンドを確認するには「ヘルプ」と入力してください。`,
            timestamp: new Date()
          };
          setChatHistory(prev => [...prev, aiMessage]);
        }
        setIsProcessing(false);
      }, 800);
    }
  };

  const getSuggestionButtons = () => {
    const suggestions: Record<AgentId, string[]> = {
      'demand': ['予測を実行', 'CSVでエクスポート', '精度を確認'],
      'md': ['計画を生成', '価格を最適化', 'シナリオを比較'],
      'inventory': ['在庫を最適化', '発注を提案', 'ABC分析を実行'],
      'planogram': ['棚割りを最適化', 'ビジュアルを表示', '効果を測定'],
      'creative': ['デザインを生成', 'テンプレートを選択', 'バッチ処理を開始'],
      'marketing': ['キャンペーンを作成', 'ROIを分析', '顧客をセグメント'],
      'alerts': ['アラートを設定', 'ルールを確認', '履歴を表示']
    };
    return suggestions[activeAgent] || [];
  };

  return (
    <div 
      className={`border-l transition-all duration-300 ease-in-out ${isOpen ? 'w-80' : 'w-12'}`}
      style={{ borderColor: colors.ui.border, backgroundColor: colors.ui.bg }}
    >
      <div className="h-full flex flex-col">
        {/* ヘッダー */}
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: colors.ui.border }}>
          {isOpen && (
            <div className="flex items-center gap-2">
              <Bot size={18} style={{ color: colors.accent.blue }} />
              <h3 className="font-semibold text-sm" style={{ color: colors.text.primary }}>
                AIアシスタント
              </h3>
              <Sparkles size={14} className="text-yellow-500" />
            </div>
          )}
          <button onClick={() => setIsOpen(!isOpen)} className="p-1 rounded hover:bg-gray-100">
            <ChevronRight 
              size={16} 
              style={{ color: colors.text.secondary }}
              className={`transform transition-transform duration-300 ${isOpen ? '' : 'rotate-180'}`}
            />
          </button>
        </div>
        
        {isOpen && (
          <>
            {/* チャット履歴 */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-3">
                {chatHistory.map(msg => (
                  <div key={msg.id} className={`${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                    {msg.type === 'user' ? (
                      <div className="inline-block max-w-[80%] p-2 rounded-lg bg-blue-500 text-white text-sm">
                        {msg.message}
                      </div>
                    ) : msg.type === 'action' ? (
                      <Card className="inline-block max-w-[90%] p-3">
                        <div className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
                              {msg.message}
                            </div>
                            {msg.actionResult && msg.actionResult.details && (
                              <div className="space-y-1 text-xs" style={{ color: colors.text.secondary }}>
                                {Object.entries(msg.actionResult.details).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span>{key}:</span>
                                    <span className="font-medium">{String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <div className="inline-block max-w-[90%] p-2 rounded-lg bg-gray-100 text-sm" style={{ color: colors.text.primary }}>
                        <div className="whitespace-pre-wrap">{msg.message}</div>
                      </div>
                    )}
                    <div className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                      {msg.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
                {isProcessing && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: colors.text.secondary }}>
                    <div className="animate-pulse">処理中...</div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* クイック提案ボタン */}
              {showSuggestions && chatHistory.length === 1 && (
                <div className="mt-4">
                  <div className="text-xs mb-2" style={{ color: colors.text.secondary }}>
                    クイックアクション:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {getSuggestionButtons().map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => {
                          setChatInput(suggestion);
                          setShowSuggestions(false);
                        }}
                        className="px-2 py-1 text-xs rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 入力フォーム */}
            <form onSubmit={handleChatSubmit} className="p-3 border-t" style={{ borderColor: colors.ui.border }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="コマンドや質問を入力..."
                  disabled={isProcessing}
                  className="flex-1 px-3 py-2 text-sm border rounded-lg outline-none focus:border-blue-500"
                  style={{ borderColor: colors.ui.border }}
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || isProcessing}
                  className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
              <div className="mt-2 text-xs" style={{ color: colors.text.secondary }}>
                Enterで送信 • 「ヘルプ」でコマンド一覧
              </div>
            </form>

            {/* システムステータス */}
            <div className="px-3 pb-3">
              <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                <div className="flex items-center gap-1">
                  <Database size={12} style={{ color: colors.text.secondary }} />
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    システム
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    接続中
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AIAssistantPanel;